import React from "react";
import { Dropdown, DropdownProps, Label, Option, OptionGroup, Spinner, makeStyles, mergeClasses } from "@fluentui/react-components";
import { card, colors, useTextStyles } from "../styles";

interface TOption {
  value: string;
  label: string;
  group?: string;
  disabled?: boolean;
}

export interface FwDropdownProps extends Omit<DropdownProps, "children"> {
  label?: string;
  emptyLabel?: string;
  options: Array<any>;
  groupBy?: boolean;
  isLoading?: boolean;
  idValue?: string;
  idLabel?: string;
}

export const FwDropdown: React.FC<FwDropdownProps> = ({
  label,
  emptyLabel,
  options,
  groupBy = false,
  isLoading = false,
  idLabel = "label",
  idValue = "value",
  ...dropdownProps
}) => {
  const styles = useFwDropdownStyles();
  const textStyles = useTextStyles();
  const dropdownId = dropdownProps.id ?? "dropdown";

  return (
    <div className={dropdownProps.className ?? styles.container}>
      {label && (
        <Label className={textStyles.label} style={{ width: "100%" }}>
          {label}
        </Label>
      )}

      {/* Loading view */}
      {isLoading && (
        <div className={styles.loadingContainer}>
          <Spinner size="small" />
          <span>Loading...</span>
        </div>
      )}

      {/* Empty view */}
      {!isLoading && options.length === 0 && <div className={styles.emptyState}>{emptyLabel || "No options available"}</div>}

      {/* Dropdown */}
      {!isLoading && options.length > 0 && (
        <Dropdown id={dropdownId} {...dropdownProps} className={mergeClasses(styles.dropdown, dropdownProps.className || "")}>
          {renderOptions(groupBy, options, idLabel, idValue)}
        </Dropdown>
      )}
    </div>
  );
};

const renderOptions = (groupBy: boolean, options: Array<TOption>, idLabel: string, idValue: string) => {
  if (!groupBy) {
    return options.map((option) => (
      <Option key={option[idValue]} value={option[idValue]} disabled={option.disabled}>
        {option[idLabel]}
      </Option>
    ));
  }

  // if groupBy is true
  const groupedOptions = options.reduce(
    (acc, option) => {
      const groupKey = option.group || "Ungrouped";
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(option);
      return acc;
    },
    {} as Record<string, TOption[]>
  );

  return Object.entries(groupedOptions).map(([groupName, groupOptions]) => (
    <OptionGroup key={groupName} label={groupName}>
      {groupOptions.map((option) => (
        <Option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </Option>
      ))}
    </OptionGroup>
  ));
};

const useFwDropdownStyles = makeStyles({
  container: {
    ...card,
    flexDirection: "column",
    gap: "5px",
    padding: "20px 10px",
    width: "inherit",
    textAlign: "start"
  },
  dropdown: {
    width: "100%",
    minWidth: "unset",
    borderRadius: "6px",
    border: `1px solid ${colors.borderColor}`,
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    transition: "border-color 0.2s, box-shadow 0.2s"
  },
  loadingContainer: {
    width: "-webkit-fill-available",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "6px 12px",
    backgroundColor: "#F9F9F9",
    borderRadius: "6px",
    border: `1px solid ${colors.borderColor}`
  },
  emptyState: {
    padding: "12px",
    backgroundColor: "#F9F9F9",
    borderRadius: "6px",
    border: `1px solid ${colors.borderColor}`,
    color: "#666",
    textAlign: "center"
  }
});

export default FwDropdown;
